import { FilterDimension, FilterValue } from '../store/enhancedFiltersSlice';
import { UserRole } from '../types';
import { hasPermission } from '../utils/roleBasedAccess';
import { 
  getAllDepartments, 
  getAllLocations, 
  getAllJobLevels,
  generateLocalizedName
} from '../utils/localizedMockData';

// Mock data sources
const mockEmployeeData = [
  { id: 1, country: 'us', businessUnit: 'engineering', department: 'backend', level: 'senior', gender: 'M', performance: 'high', attritionRisk: 'low' },
  { id: 2, country: 'uk', businessUnit: 'sales', department: 'enterprise', level: 'manager', gender: 'F', performance: 'medium', attritionRisk: 'medium' },
  { id: 3, country: 'de', businessUnit: 'marketing', department: 'digital', level: 'junior', gender: 'M', performance: 'high', attritionRisk: 'low' },
  // ... more mock data would be here
];

interface FilterCache {
  [key: string]: {
    data: FilterValue[];
    timestamp: number;
    ttl: number;
  };
}

class FilterService {
  private cache: FilterCache = {};
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  
  /**
   * Get available values for a filter dimension with RBAC checks
   */
  async getAvailableValues(
    dimension: FilterDimension,
    currentRole: UserRole,
    parentFilters?: Partial<Record<FilterDimension, string[]>>
  ): Promise<FilterValue[]> {
    // Check cache first
    const cacheKey = this.getCacheKey(dimension, parentFilters);
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    // RBAC checks for sensitive dimensions
    if (!this.canAccessDimension(dimension, currentRole)) {
      return [];
    }

    // Simulate API call delay
    await this.simulateDelay();

    // Get values based on dimension
    let values: FilterValue[] = [];
    
    switch (dimension) {
      case 'country':
        values = await this.getCountryValues(parentFilters);
        break;
      case 'businessUnit':
        values = await this.getBusinessUnitValues(parentFilters);
        break;
      case 'department':
        values = await this.getDepartmentValues(parentFilters);
        break;
      case 'level':
        values = await this.getLevelValues(parentFilters);
        break;
      case 'manager':
        values = await this.getManagerValues(parentFilters);
        break;
      case 'team':
        values = await this.getTeamValues(parentFilters);
        break;
      case 'gender':
        values = await this.getGenderValues(parentFilters);
        break;
      case 'performanceBand':
        values = await this.getPerformanceValues(parentFilters);
        break;
      case 'attritionRiskBand':
        values = await this.getAttritionRiskValues(parentFilters);
        break;
      default:
        values = [];
    }

    // Apply privacy threshold
    values = this.applyPrivacyThreshold(values);

    // Cache the results
    this.setCache(cacheKey, values);

    return values;
  }

  /**
   * Get filtered employee count based on active filters
   */
  async getFilteredCount(
    filters: Partial<Record<FilterDimension, string[]>>,
    currentRole: UserRole
  ): Promise<number> {
    // Simulate API call
    await this.simulateDelay();

    // Apply filters to mock data
    let filteredData = [...mockEmployeeData];

    Object.entries(filters).forEach(([dimension, values]) => {
      if (values && values.length > 0) {
        filteredData = filteredData.filter(employee => {
          const employeeValue = this.getEmployeeValue(employee, dimension as FilterDimension);
          return values.includes(employeeValue);
        });
      }
    });

    // Apply RBAC data restrictions
    filteredData = this.applyRBACRestrictions(filteredData, currentRole);

    return filteredData.length;
  }

  /**
   * Check if filtered results meet privacy threshold
   */
  isPrivacyProtected(count: number, threshold: number = 5): boolean {
    return count > 0 && count < threshold;
  }

  /**
   * Validate filter dependencies
   */
  validateDependencies(
    filters: Partial<Record<FilterDimension, string[]>>,
    dependencies: Partial<Record<FilterDimension, FilterDimension[]>>
  ): boolean {
    for (const [dimension, dependentDimensions] of Object.entries(dependencies)) {
      if (filters[dimension as FilterDimension]) {
        // Check if dependent dimensions are also filtered appropriately
        for (const dependent of dependentDimensions || []) {
          if (filters[dependent] && filters[dependent]!.length > 0) {
            // Validate that dependent values are valid for parent
            // This would involve checking actual data relationships
            console.log(`Validating ${dependent} depends on ${dimension}`);
          }
        }
      }
    }
    return true;
  }

  // Private helper methods

  private canAccessDimension(dimension: FilterDimension, role: UserRole): boolean {
    const restrictedDimensions: Partial<Record<FilterDimension, string>> = {
      gender: 'canViewSensitiveMetrics',
      performanceBand: 'canViewSensitiveMetrics',
      attritionRiskBand: 'canViewAttritionPredictions',
    };

    const permission = restrictedDimensions[dimension];
    if (!permission) return true;
    
    return hasPermission(role, permission as any);
  }

  private async getCountryValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    const locations = getAllLocations();
    return locations.slice(0, 8).map((location, index) => ({
      id: location.toLowerCase().replace(/[^a-z]/g, '').substring(0, 2),
      label: location,
      count: Math.floor(3500 - (index * 400) + Math.random() * 200)
    }));
  }

  private async getBusinessUnitValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    const allUnits = [
      { id: 'engineering', label: 'Engineering', count: 1832 },
      { id: 'sales', label: 'Sales', count: 923 },
      { id: 'marketing', label: 'Marketing', count: 612 },
      { id: 'hr', label: 'Human Resources', count: 287 },
      { id: 'finance', label: 'Finance', count: 394 },
      { id: 'operations', label: 'Operations', count: 651 },
      { id: 'product', label: 'Product', count: 489 },
      { id: 'legal', label: 'Legal', count: 124 },
    ];

    // Filter based on parent country if provided
    if (parentFilters?.country && parentFilters.country.length > 0) {
      // Simulate filtering based on country
      return allUnits.map(unit => ({
        ...unit,
        count: Math.floor(unit.count * 0.7) // Reduced count for filtered context
      }));
    }

    return allUnits;
  }

  private async getDepartmentValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    const departments = [
      { id: 'backend', label: 'Backend Engineering', count: 543, parentId: 'engineering' },
      { id: 'frontend', label: 'Frontend Engineering', count: 421, parentId: 'engineering' },
      { id: 'devops', label: 'DevOps', count: 234, parentId: 'engineering' },
      { id: 'enterprise', label: 'Enterprise Sales', count: 345, parentId: 'sales' },
      { id: 'smb', label: 'SMB Sales', count: 278, parentId: 'sales' },
      { id: 'digital', label: 'Digital Marketing', count: 312, parentId: 'marketing' },
      { id: 'brand', label: 'Brand Marketing', count: 189, parentId: 'marketing' },
    ];

    // Filter based on parent business unit
    if (parentFilters?.businessUnit && parentFilters.businessUnit.length > 0) {
      return departments.filter(dept => 
        parentFilters.businessUnit!.includes(dept.parentId!)
      );
    }

    return departments;
  }

  private async getLevelValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    return [
      { id: 'intern', label: 'Intern', count: 145 },
      { id: 'junior', label: 'Junior', count: 892 },
      { id: 'mid', label: 'Mid-Level', count: 1543 },
      { id: 'senior', label: 'Senior', count: 1234 },
      { id: 'staff', label: 'Staff', count: 567 },
      { id: 'principal', label: 'Principal', count: 234 },
      { id: 'manager', label: 'Manager', count: 432 },
      { id: 'director', label: 'Director', count: 198 },
      { id: 'vp', label: 'Vice President', count: 67 },
      { id: 'svp', label: 'Senior VP', count: 23 },
      { id: 'c-suite', label: 'C-Suite', count: 8 },
    ];
  }

  private async getManagerValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    const managers = [
      { id: 'john-smith', label: 'John Smith', count: 45 },
      { id: 'sarah-johnson', label: 'Sarah Johnson', count: 38 },
      { id: 'michael-chen', label: 'Michael Chen', count: 52 },
      { id: 'emily-davis', label: 'Emily Davis', count: 41 },
      { id: 'robert-wilson', label: 'Robert Wilson', count: 29 },
    ];

    // Apply department filter if present
    if (parentFilters?.department && parentFilters.department.length > 0) {
      // Simulate filtering managers by department
      return managers.slice(0, 3);
    }

    return managers;
  }

  private async getTeamValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    return [
      { id: 'platform', label: 'Platform Team', count: 23 },
      { id: 'mobile', label: 'Mobile Team', count: 18 },
      { id: 'data', label: 'Data Team', count: 31 },
      { id: 'infrastructure', label: 'Infrastructure Team', count: 27 },
      { id: 'growth', label: 'Growth Team', count: 19 },
    ];
  }

  private async getGenderValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    return [
      { id: 'M', label: 'Male', count: 3421 },
      { id: 'F', label: 'Female', count: 2876 },
      { id: 'NB', label: 'Non-Binary', count: 43 },
      { id: 'ND', label: 'Prefer Not to Disclose', count: 132 },
    ];
  }

  private async getPerformanceValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    return [
      { id: 'exceptional', label: 'Exceptional', count: 543 },
      { id: 'high', label: 'High Performer', count: 1876 },
      { id: 'solid', label: 'Solid Performer', count: 2341 },
      { id: 'developing', label: 'Developing', count: 987 },
      { id: 'needs-improvement', label: 'Needs Improvement', count: 234 },
    ];
  }

  private async getAttritionRiskValues(parentFilters?: Partial<Record<FilterDimension, string[]>>): Promise<FilterValue[]> {
    return [
      { id: 'very-low', label: 'Very Low Risk', count: 2134 },
      { id: 'low', label: 'Low Risk', count: 1876 },
      { id: 'medium', label: 'Medium Risk', count: 1234 },
      { id: 'high', label: 'High Risk', count: 567 },
      { id: 'very-high', label: 'Very High Risk', count: 234 },
    ];
  }

  private getEmployeeValue(employee: any, dimension: FilterDimension): string {
    const mapping: Record<string, string> = {
      country: employee.country,
      businessUnit: employee.businessUnit,
      department: employee.department,
      level: employee.level,
      gender: employee.gender,
      performanceBand: employee.performance,
      attritionRiskBand: employee.attritionRisk,
    };
    return mapping[dimension] || '';
  }

  private applyRBACRestrictions(data: any[], role: UserRole): any[] {
    // Apply role-based data restrictions
    switch (role) {
      case 'MANAGER':
        // Managers only see their team's data
        return data.slice(0, Math.floor(data.length * 0.3));
      case 'HR_LEADER':
        // HR Leaders see everything
        return data;
      default:
        return data.slice(0, Math.floor(data.length * 0.2));
    }
  }

  private applyPrivacyThreshold(values: FilterValue[]): FilterValue[] {
    return values.map(value => {
      if (value.count && value.count < 5) {
        // Mask small counts for privacy
        return { ...value, count: undefined, disabled: true };
      }
      return value;
    });
  }

  private getCacheKey(dimension: FilterDimension, parentFilters?: any): string {
    const filterKey = parentFilters ? JSON.stringify(parentFilters) : '';
    return `${dimension}_${filterKey}`;
  }

  private getFromCache(key: string): FilterValue[] | null {
    const cached = this.cache[key];
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }

    return cached.data;
  }

  private setCache(key: string, data: FilterValue[]): void {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl: this.DEFAULT_TTL,
    };
  }

  private async simulateDelay(): Promise<void> {
    // Simulate network delay
    return new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache = {};
  }

  /**
   * Export current filter state
   */
  exportFilterState(filters: Partial<Record<FilterDimension, string[]>>): string {
    return JSON.stringify(filters, null, 2);
  }

  /**
   * Import filter state from JSON
   */
  importFilterState(jsonString: string): Partial<Record<FilterDimension, string[]>> {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Invalid filter state JSON:', error);
      return {};
    }
  }

  /**
   * Generate shareable URL with filter state
   */
  generateShareableUrl(filters: Partial<Record<FilterDimension, string[]>>): string {
    const baseUrl = window.location.origin + window.location.pathname;
    const filterParams = btoa(JSON.stringify(filters));
    return `${baseUrl}?filters=${filterParams}`;
  }

  /**
   * Parse filters from URL
   */
  parseFiltersFromUrl(url: string): Partial<Record<FilterDimension, string[]>> {
    try {
      const urlParams = new URLSearchParams(new URL(url).search);
      const filterParams = urlParams.get('filters');
      if (filterParams) {
        return JSON.parse(atob(filterParams));
      }
    } catch (error) {
      console.error('Error parsing filters from URL:', error);
    }
    return {};
  }
}

// Export singleton instance
export default new FilterService();